import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { formatDistanceToNow } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import type { CommunityRequest } from '../../shared/services/requests.service';

/** Posts younger than this surface a "NEW" badge — caps at a day so the
 *  page never looks stale. */
const NEW_BADGE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Public card shown in the requests feed and the "my requests" page. Each
 * card has a visual header (image or themed gradient placeholder) with
 * floating badges, then a content block + a primary CTA.
 */
export function RequestCard({ request, compact }: { request: CommunityRequest; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;

  const createdAtMs = new Date(request.createdAt).getTime();
  const postedAgo = formatDistanceToNow(createdAtMs, {
    addSuffix: true,
    locale: dateLocale,
  });
  const isNew = Date.now() - createdAtMs < NEW_BADGE_WINDOW_MS;

  const location = [request.cityName, request.countryCode]
    .filter(Boolean)
    .join(' • ');

  return (
    <Card
      component={RouterLink}
      to={`/requests/${request.id}`}
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 16px 32px rgba(0,0,0,0.10)',
          borderColor: 'primary.main',
        },
        '&:hover .request-card-cta': {
          bgcolor: 'primary.main',
          color: 'common.white',
          borderColor: 'primary.main',
        },
      }}
    >
      {/* HEADER — image when available, themed gradient placeholder otherwise.
          Always rendered so every card has the same visual weight in the grid. */}
      <Box
        sx={{
          position: 'relative',
          height: 150,
          background: (theme) =>
            request.imageUrl
              ? undefined
              : `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.secondary.main}22 50%, ${theme.palette.primary.main}33 100%)`,
          backgroundImage: request.imageUrl ? `url(${request.imageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!request.imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              opacity: 0.55,
            }}
          >
            <HelpOutlineRoundedIcon sx={{ fontSize: 64 }} />
          </Box>
        )}

        {/* Dark gradient overlay — only over images, to keep the floating
            chips legible regardless of the photo. */}
        {request.imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.35) 100%)',
            }}
          />
        )}

        {/* TOP-LEFT — NEW + status badges. */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ position: 'absolute', top: 12, left: 12 }}
        >
          {isNew && request.status === 'ACTIVE' && (
            <Chip
              icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '0.95rem !important' }} />}
              label={t('requests.newBadge')}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                fontWeight: 700,
                height: 24,
                '& .MuiChip-icon': { color: 'common.white' },
              }}
            />
          )}
          {request.status !== 'ACTIVE' && (
            <Chip
              size="small"
              label={t(`requests.status.${request.status.toLowerCase()}`)}
              sx={{
                bgcolor: request.status === 'RESOLVED' ? 'success.main' : 'grey.700',
                color: 'common.white',
                fontWeight: 700,
                height: 24,
              }}
            />
          )}
        </Stack>

        {/* TOP-RIGHT — response counter for social proof. Only shown when
            there's actually something to show. */}
        {request.responseCount > 0 && (
          <Chip
            icon={<GroupRoundedIcon sx={{ fontSize: '0.95rem !important' }} />}
            label={t('requests.responsesCount', { count: request.responseCount })}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: 'rgba(255,255,255,0.95)',
              color: 'text.primary',
              fontWeight: 700,
              height: 24,
              backdropFilter: 'blur(4px)',
            }}
          />
        )}

        {/* BOTTOM-RIGHT — relative posted time, subdued. */}
        <Chip
          icon={<AccessTimeRoundedIcon sx={{ fontSize: '0.95rem !important' }} />}
          label={postedAgo}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: 'rgba(255,255,255,0.9)',
            color: 'text.secondary',
            height: 24,
            backdropFilter: 'blur(4px)',
          }}
        />
      </Box>

      <CardContent
        sx={{
          flex: 1,
          p: compact ? 2 : 2.5,
          pb: '0 !important',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 1,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {request.title}
        </Typography>

        {location && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ color: 'text.secondary', mb: 1.5 }}
          >
            <LocationOnRoundedIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {location}
            </Typography>
          </Stack>
        )}

        {request.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
            }}
          >
            {request.description}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ p: compact ? 2 : 2.5, pt: 2 }}>
        <Button
          className="request-card-cta"
          component="span"
          variant="outlined"
          fullWidth
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            fontWeight: 700,
            transition: 'all 0.2s ease',
          }}
        >
          {t('requests.viewAction')}
        </Button>
      </CardActions>
    </Card>
  );
}
